import { LocationStrategy } from '@angular/common';
import {
    afterNextRender,
    Attribute,
    booleanAttribute,
    DestroyRef,
    Directive,
    ElementRef,
    HostListener,
    inject,
    Input,
    input,
    OnChanges,
    OnDestroy,
    Renderer2,
    SimpleChanges,
    ɵɵsanitizeUrlOrResourceUrl,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Event, NavigationEnd, Router, RouterLink, RouterPreloader } from '@angular/router';
import { PreloadingRegistry } from '../utilities/preload';

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: '[hyundaiRouterLink]',
    standalone: true,
    providers: [{ provide: RouterLink, useExisting: RouterLinkDirective }],
})
export class RouterLinkDirective extends RouterLink implements OnChanges, OnDestroy {
    externalNewTab = input(true);

    registry = inject(PreloadingRegistry);
    loader = inject(RouterPreloader);
    destroyRef = inject(DestroyRef);

    blockTriggerPreloading = input(false, { transform: booleanAttribute });

    protected externalLink = '';
    protected hyuIsAnchorElement: boolean;

    intersectionObserver?: IntersectionObserver;

    constructor(
        private hyuRouter: Router,
        private hyuRoute: ActivatedRoute,
        private hyuRenderer: Renderer2,
        private hyuEl: ElementRef,
        private hyuLocationStrategy?: LocationStrategy,
        @Attribute('tabindex')
        hyuTabIndexAttribute?: string | null,
    ) {
        super(hyuRouter, hyuRoute, hyuTabIndexAttribute, hyuRenderer, hyuEl, hyuLocationStrategy);

        const tagName = hyuEl.nativeElement.tagName;
        this.hyuIsAnchorElement = tagName === 'A' || tagName === 'AREA';
        this.ngOnDestroy();
        if (this.hyuIsAnchorElement) {
            hyuRouter.events.pipe(takeUntilDestroyed()).subscribe((s: Event) => {
                if (s instanceof NavigationEnd) {
                    this.hyuUpdateHref();
                }
            });
        }

        afterNextRender(() => {
            if (this.blockTriggerPreloading()) return;

            this.onVisible(hyuEl.nativeElement, () => {
                this.registerPreload();
            });
        });
    }

    @Input()
    set hyundaiRouterLink(commands: unknown[] | string | null | undefined) {
        if (typeof commands === 'string' && /^(https?:\/\/|(mailto|tel):)/.test(commands)) {
            this.externalLink = commands;
        } else {
            this.routerLink = commands;
        }
    }

    @HostListener('click', ['$event.button', '$event.ctrlKey', '$event.shiftKey', '$event.altKey', '$event.metaKey'])
    override onClick(button: number, ctrlKey: boolean, shiftKey: boolean, altKey: boolean, metaKey: boolean): boolean {
        if (this.externalLink && !this.hyuIsAnchorElement) {
            window.open(this.externalLink, this.externalNewTab() ? '_blank' : '_self');
            return true;
        }

        return super.onClick(button, ctrlKey, shiftKey, altKey, metaKey);
    }

    registerPreload() {
        if (!this.urlTree) return;
        this.registry.addLink(this.urlTree);
        this.loader.preload().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }

    override ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        if (this.externalLink && this.hyuIsAnchorElement) {
            this.hyuUpdateHref();
            if (this.externalNewTab()) {
                this.target = '_blank';
            }
        }
    }

    private hyuUpdateHref(): void {
        if (this.externalLink) {
            this.href = this.externalLink;
            this.hyuApplyAttributeValue('href', this.href);
        } else {
            this.href =
                this.urlTree !== null && this.hyuLocationStrategy
                    ? this.hyuLocationStrategy?.prepareExternalUrl(this.hyuRouter.serializeUrl(this.urlTree))
                    : null;

            const sanitizedValue =
                this.href === null
                    ? null
                    : ɵɵsanitizeUrlOrResourceUrl(this.href, this.hyuEl.nativeElement.tagName.toLowerCase(), 'href');
            this.hyuApplyAttributeValue('href', sanitizedValue);
        }
    }

    private hyuApplyAttributeValue(attrName: string, attrValue: string | null) {
        const renderer = this.hyuRenderer;
        const nativeElement = this.hyuEl.nativeElement;
        if (attrValue === null) {
            renderer.removeAttribute(nativeElement, attrName);
        } else {
            renderer.setAttribute(nativeElement, attrName, attrValue);
        }
    }

    onVisible(element: Element, callback: () => void) {
        this.intersectionObserver = new IntersectionObserver((entries, observer) => {
            for (const entry of entries) {
                if (entry.intersectionRatio > 0) {
                    callback();
                    observer.disconnect();
                }
            }
        });

        this.intersectionObserver?.observe(element);
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
        this.intersectionObserver?.disconnect();
    }
}
